import type { BadgeTone } from '@/components/ui/Badge';
import type {
  EntranceKind,
  EntranceResult,
  RefuseReason,
  TicketBlockReason,
  TicketEmailChangeActor,
  TicketKind,
  TicketStatus,
  UserStatus,
  UserType,
  VoucherDiscountType,
  LoyaltyLedgerReason,
  VoucherRuleTrigger,
  VoucherStatus,
  VoucherValidityMode,
} from '@/gql/graphql';

export function userDisplayName(user: {
  firstName: string;
  lastName?: string | null;
  email: string;
}): string {
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return full || user.email;
}

export function userStatusLabel(status: UserStatus): string {
  switch (status) {
    case 'IMPORTED':
      return 'Imported';
    case 'PROVISIONAL':
      return 'Provisional';
    case 'CREATED':
      return 'Created';
    case 'ACTIVE':
      return 'Active';
    case 'BLOCKED':
      return 'Blocked';
    case 'DELETED':
      return 'Deleted';
    default:
      return status;
  }
}

export function userStatusTone(status: UserStatus): BadgeTone {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'CREATED':
      return 'warning';
    case 'IMPORTED':
    case 'PROVISIONAL':
      return 'info';
    case 'BLOCKED':
      return 'error';
    case 'DELETED':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function userTypeLabel(type: UserType | null | undefined): string {
  if (!type) return 'Customer';
  switch (type) {
    case 'EMPLOYEE':
      return 'Employee';
    case 'CONTRACTOR':
      return 'Contractor';
    case 'ADMIN':
      return 'Admin';
    default:
      return type;
  }
}

export function voucherStatusLabel(status: VoucherStatus): string {
  switch (status) {
    case 'ISSUED':
      return 'Issued';
    case 'RESERVED':
      return 'Reserved';
    case 'REDEEMED':
      return 'Redeemed';
    case 'EXPIRED':
      return 'Expired';
    case 'REVOKED':
      return 'Revoked';
    case 'FORFEITED':
      return 'Forfeited';
    default:
      return status;
  }
}

export function voucherStatusTone(status: VoucherStatus): BadgeTone {
  switch (status) {
    case 'ISSUED':
      return 'success';
    case 'RESERVED':
      return 'warning';
    case 'REDEEMED':
      return 'info';
    case 'EXPIRED':
      return 'neutral';
    case 'REVOKED':
      return 'error';
    case 'FORFEITED':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function voucherTriggerLabel(trigger: VoucherRuleTrigger): string {
  switch (trigger) {
    case 'POINTS_THRESHOLD':
      return 'Points threshold';
    case 'FIRST_CARD_LINKED':
      return 'First card linked';
    case 'BIRTHDAY':
      return 'Birthday';
    case 'TICKET_CLAIMED':
      return 'Ticket claimed';
    default:
      return trigger;
  }
}

export function voucherValidityModeLabel(mode: VoucherValidityMode): string {
  switch (mode) {
    case 'AFTER_CLAIM':
      return 'After claim';
    case 'NIGHT':
      return 'Night';
    case 'AFTER_NIGHT':
      return 'After night';
    default:
      return mode;
  }
}

export function voucherDiscountLabel(type: VoucherDiscountType): string {
  switch (type) {
    case 'PERCENTAGE':
      return 'Percentage off';
    case 'AMOUNT':
      return 'Fixed amount';
    case 'FREE_PRODUCT':
      return 'Free product';
    default:
      return type;
  }
}

export function entranceKindLabel(kind: EntranceKind): string {
  switch (kind) {
    case 'CLAIMED':
      return 'Claimed';
    case 'GOLDEN':
      return 'Golden';
    case 'DOOR':
      return 'Door';
    default:
      return kind;
  }
}

export function entranceResultLabel(result: EntranceResult): string {
  switch (result) {
    case 'VALID':
      return 'Admitted';
    case 'REFUSED':
      return 'Refused';
    case 'ALREADY_SCANNED':
      return 'Already scanned';
    case 'EXPIRED_QR':
      return 'Expired QR';
    case 'DECRYPT_FAILED':
      return 'Decrypt failed';
    case 'NO_DOORS_WINDOW':
      return 'No doors window';
    case 'WRONG_NIGHT':
    case 'WRONG_PRODUCTION':
      return 'Wrong night';
    case 'BLOCKED':
      return 'Blocked';
    default:
      return result;
  }
}

export function entranceResultTone(result: EntranceResult): BadgeTone {
  switch (result) {
    case 'VALID':
      return 'success';
    case 'REFUSED':
    case 'BLOCKED':
      return 'error';
    case 'ALREADY_SCANNED':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function refuseReasonLabel(reason: RefuseReason | null | undefined): string {
  if (!reason) return '—';
  switch (reason) {
    case 'TOO_INTOXICATED':
      return 'Too intoxicated';
    case 'ID_MISMATCH':
      return 'ID mismatch';
    case 'BEHAVIOUR_AT_DOOR':
      return 'Behaviour at door';
    case 'OTHER':
      return 'Other';
    default:
      return reason;
  }
}

export function ticketStatusLabel(status: TicketStatus): string {
  switch (status) {
    case 'HELD':
      return 'Held';
    case 'PURCHASED':
      return 'Purchased';
    case 'CLAIMED':
      return 'Claimed';
    case 'RELEASED':
      return 'Released';
    case 'REFUNDED':
      return 'Refunded';
    case 'BLOCKED':
      return 'Blocked';
    case 'IMPORTED':
      return 'Imported';
    default:
      return status;
  }
}

export function ticketStatusTone(status: TicketStatus): BadgeTone {
  switch (status) {
    case 'HELD':
      return 'warning';
    case 'PURCHASED':
      return 'info';
    case 'CLAIMED':
      return 'success';
    case 'RELEASED':
    case 'IMPORTED':
      return 'neutral';
    case 'REFUNDED':
    case 'BLOCKED':
      return 'error';
    default:
      return 'neutral';
  }
}

export function ticketBlockReasonLabel(reason: TicketBlockReason): string {
  switch (reason) {
    case 'FRAUD':
      return 'Fraud';
    case 'BANK_REFUND':
      return 'Bank refund';
    case 'WRONG_SALE':
      return 'Wrong sale';
    case 'OTHER':
      return 'Other';
    default:
      return reason;
  }
}

/** Order payment methods are free strings; only `stager` (imported history) is relabelled. */
export function paymentMethodLabel(method: string | null | undefined): string {
  return method === 'stager' ? 'Stager' : (method ?? '');
}

export function ticketKindLabel(kind: TicketKind): string {
  switch (kind) {
    case 'ONLINE':
      return 'Online';
    case 'MEMBER':
      return 'Member';
    case 'DOOR':
      return 'Door';
    case 'POS':
      return 'POS';
    case 'GUEST_LIST':
      return 'Guest';
    default:
      return kind;
  }
}

export function ticketEmailChangeActorLabel(actor: TicketEmailChangeActor): string {
  switch (actor) {
    case 'BUYER':
      return 'Buyer';
    case 'STAFF':
      return 'Staff';
    case 'CLAIMER':
      return 'Claimer';
    default:
      return actor;
  }
}

export function loyaltyLedgerReasonLabel(reason: LoyaltyLedgerReason): string {
  switch (reason) {
    case 'PAYMENT':
      return 'Payment';
    case 'TICKET':
      return 'Ticket';
    case 'REFUND':
      return 'Refund';
    case 'ADMIN_ADJUSTMENT':
      return 'Manual adjustment';
    case 'VOUCHER_REDEEM':
      return 'Reward';
    default:
      return reason;
  }
}

export const CUSTOMER_IO_KIND_LABELS: Record<string, string> = {
  MAGIC_LINK: 'Magic link',
  RECEIPT: 'Receipt',
  TICKET_MANAGE_CLAIM: 'Ticket manage / claim',
  TICKET_CLAIM_REMINDER_HOLDER: 'Ticket claim reminder (friend)',
  TICKET_QR: 'Ticket QR',
  COMPANION_TICKETS: 'Companion tickets',
  MEMBER_SALE_INVITE: 'Member sale invite',
  GUEST_LIST_INVITE: 'Guest list invite',
  VOUCHER: 'Voucher',
  CONTACT_GUEST: 'Contact — guest',
  CONTACT_STAFF: 'Contact — club',
  PAYMENT_FAILED: 'Payment failed',
  GOLDEN_TICKET_ASSIGNED: 'Golden ticket',
  CLAIM_LINK_REVOKED: 'Claim link revoked',
  TICKET_HANDED_OVER: 'Ticket handed over',
  VOUCHER_EXPIRY_REMINDER: 'Voucher expiry reminder',
};

/** Inline messages for the required doors window of a night and the required default doors of a template. */
export const DOORS_WINDOW_REQUIRED = 'Doors window is required';
export const DOORS_DEFAULTS_REQUIRED = 'Doors defaults are required';

/**
 * Go refuses a missing or inverted doors window as `BAD_USER_INPUT` with
 * `extensions.reason` `DOORS_WINDOW_REQUIRED`. Null for any other reason.
 */
export function doorsReasonLabel(reason: string | undefined): string | null {
  return reason === 'DOORS_WINDOW_REQUIRED' ? DOORS_WINDOW_REQUIRED : null;
}
